import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button, { type IButtonProps } from '@orn/shared-web/components/Button/Button';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { PlaidLinkTokenReplaceRefusal, type LinkTokenIntent } from '../../../__generated__/globalTypes';
import PlaidConnectionModal from '../PlaidConnectionModal';
import { useAccountLink, type LinkedFinancialAccounts } from '../useAccountLink';

/**
 * Why a session opened to replace a connection was refused, in the
 * household's terms. Each names the one thing that unblocks it.
 */
export const replaceRefusalMessage = (refusal: PlaidLinkTokenReplaceRefusal): string => {
  switch (refusal) {
    case PlaidLinkTokenReplaceRefusal.PaymentMethod:
      return 'Your current payment method debits through this connection. Switch to an account at another bank or a card first, then reconnect.';
    case PlaidLinkTokenReplaceRefusal.AccountsUnmatched:
      return 'We could not match the reconnected accounts to the ones you have. Choose another account.';
    case PlaidLinkTokenReplaceRefusal.ConnectionGone:
      return 'This connection is no longer available. Choose one of your current accounts.';
    default: {
      const unhandled: never = refusal;
      throw new Error(`Unhandled replace refusal: ${String(unhandled)}`);
    }
  }
};

const getErrorMessage = (
  errorCode: ApiErrorCode | null | undefined,
  replaceRefusal: PlaidLinkTokenReplaceRefusal | null = null
) => {
  if (!errorCode) {
    return null;
  }
  if (replaceRefusal !== null) {
    return replaceRefusalMessage(replaceRefusal);
  }

  switch (errorCode) {
    case ApiErrorCode.DUPLICATE_ITEM:
      return 'You already have a connection with this institution. Please select a different one.';
    case ApiErrorCode.PLAID_LINK_PENDING:
      return 'Your banks are still connecting. They will show up here in a minute.';
    case ApiErrorCode.INVALID_INPUT:
      // The session could not do what it was opened for — most often because
      // the institution does not offer what it was asking to widen.
      return 'This bank cannot be set up for this. Try again, or use a different account.';
    case ApiErrorCode.PLAID_LINK_TOKEN_FAILED:
    case ApiErrorCode.PLAID_EXCHANGE_FAILED:
    default:
      return 'An error occurred while connecting your account. Please try again or contact support if the issue persists.';
  }
};

interface IPlaidLinkButtonProps extends IButtonProps, React.PropsWithChildren {
  /**
   * Called once the session's accounts are persisted, with the accounts it
   * returned. Update mode can show an account picker, so that list is what
   * says which accounts the Item still holds — it is empty when no picker ran.
   */
  onSuccess?: (linkedAccounts: LinkedFinancialAccounts, context: { isReconnection: boolean }) => void;
  /**
   * Which connection the session runs against, and `intent` states what for.
   * A connection id skips the selection modal and opens Link on that
   * connection; `null` creates a new one; `undefined` opens the modal.
   */
  bankConnectionId?: string | null;
  /** What the session against `bankConnectionId` is for; omitted, it is a reconnect. */
  intent?: LinkTokenIntent;
  /** A held connection a fresh link to its institution replaces; skips the modal like a reconnection. */
  replacesBankConnectionId?: string;
}

/**
 * Button that launches the Plaid account-linking flow. Clicking it opens the
 * connection-selection modal (or jumps straight to Plaid for a reconnection
 * when `bankConnectionId` is supplied); the modal → Plaid → linked-accounts state
 * machine lives in {@link useAccountLink}, shared with the assistant's
 * `financial_account_connect` flow. When the flow skips the modal, link errors
 * surface inline above the button rather than in the modal.
 */
const PlaidLinkButton = ({
  onSuccess,
  children,
  className,
  bankConnectionId,
  intent,
  replacesBankConnectionId,
  ...buttonProps
}: IPlaidLinkButtonProps) => {
  const {
    trigger,
    shouldSkipModal,
    isModalOpen,
    onModalOpenChange,
    onModalSelect,
    errorCode,
    replaceRefusal,
    isLoading,
  } = useAccountLink({
    bankConnectionId,
    intent,
    replacesBankConnectionId,
    onLinked: (linkedAccounts, context) => onSuccess?.(linkedAccounts, context),
  });

  return (
    <>
      <div className="flex flex-col items-center">
        {shouldSkipModal && errorCode && (
          <div className="bg-error-secondary text-error-primary mb-6 rounded-lg px-3 py-2 text-sm">
            {getErrorMessage(errorCode, replaceRefusal)}
          </div>
        )}
        <Button
          className={cx('w-fit', className)}
          onClick={trigger}
          isLoading={isLoading}
          showTextWhileLoading
          size="sm"
          color="primary"
          {...buttonProps}
        >
          {children}
        </Button>
      </div>
      <PlaidConnectionModal
        isOpen={isModalOpen}
        onOpenChange={onModalOpenChange}
        onSelect={onModalSelect}
        errorCode={errorCode}
      />
    </>
  );
};

export default PlaidLinkButton;
